import { useState, useRef, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { HiOutlineOfficeBuilding, HiCheckCircle, HiPlus, HiSearch } from 'react-icons/hi';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAddCompaniesToProspectQuery } from '#leadOverviewFeature/models/companies/mutations';
import { useCreateProspectQuery } from '#leadOverviewFeature/models/prospectQueries/mutations';
import { useB2bHeroLeadSearchQueries } from '#leadOverviewFeature/models/prospectQueries/queries';

import { fetchPlaceDetails } from '../../api/placesApi';

// Transform Google Maps places to company entries format
function transformPlacesToCompanies(places) {
	return places.map((place) => ({
		companyName: place.name || '',
		website: place.website || place.websiteUri || null,
		phoneNumber: place.phoneNumber || place.nationalPhoneNumber || place.internationalPhoneNumber || null,
		address: place.address || place.formattedAddress || null,
		googleMapsUrl: place.googleMapsUrl || null,
		rating: place.rating || null,
		userRatingsTotal: place.userRatingsTotal || null,
		placeId: place.placeId || place.place_id || null,
	}));
}

/**
 * Fetch detailed information for all selected places.
 * Search results don't include phone/website, so we need to fetch details.
 */
async function fetchPlacesWithDetails(selectedPlaces) {
	return Promise.all(
		selectedPlaces.map(async (place) => {
			try {
				const placeId = place.placeId || place.place_id;
				if (!placeId) return place;

				const response = await fetchPlaceDetails(placeId);
				const details = response?.data || response;

				return { ...place, ...details, placeId };
			} catch {
				return place;
			}
		}),
	);
}

const QueryListItem = ({ query, isSelected, onSelect }) => {
	const className = isSelected
		? 'w-full px-4 py-3 text-left border-b border-bgBase-400 last:border-0 transition-colors bg-primary-100 border-l-4 border-l-primary-500'
		: 'w-full px-4 py-3 text-left border-b border-bgBase-400 last:border-0 transition-colors hover:bg-gray-50';

	return (
		<button key={query.id} type="button" className={className} onClick={() => onSelect(query)}>
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-text">{query.queryName}</p>
					<p className="text-xs text-bgBase-800 mt-0.5">{query.companyCount || 0} Unternehmen</p>
					{isSelected && <p className="text-xs text-primary-700 mt-0.5">&#x2713; Ausgewählt</p>}
				</div>
			</div>
		</button>
	);
};

QueryListItem.propTypes = {
	query: PropTypes.object.isRequired,
	isSelected: PropTypes.bool.isRequired,
	onSelect: PropTypes.func.isRequired,
};

const CreateQueryForm = ({
	newQueryName,
	setNewQueryName,
	errors,
	setErrors,
	isProcessing,
	isFetchingDetails,
	onCreateNew,
	onCancel,
}) => {
	const buttonLabel = isProcessing ? (isFetchingDetails ? 'Lade Details...' : 'Erstelle...') : null;

	return (
		<div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
			<h6 className="text-sm font-semibold mb-3 text-text">Neue Suche erstellen</h6>
			<div className="space-y-3">
				<div>
					<TextInput
						error={Boolean(errors.newQueryName)}
						placeholder="Titel der neuen Suche"
						value={newQueryName}
						onChange={(e) => {
							setNewQueryName(e.target.value);
							setErrors((prev) => ({ ...prev, newQueryName: undefined }));
						}}
						maxLength={100}
						autoFocus
					/>
					{errors.newQueryName && <p className="text-xs text-warning-700 mt-1">{errors.newQueryName}</p>}
				</div>
				<div className="flex gap-2">
					<Button
						variant="solid"
						size="sm"
						onClick={onCreateNew}
						disabled={isProcessing || !newQueryName.trim()}
						className="flex-1"
					>
						{isProcessing ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
								{buttonLabel}
							</>
						) : (
							<>
								<HiPlus className="mr-2" />
								Erstellen & Hinzufügen
							</>
						)}
					</Button>
					<Button variant="outline" size="sm" onClick={onCancel} disabled={isProcessing} className="flex-1">
						Abbrechen
					</Button>
				</div>
			</div>
		</div>
	);
};

CreateQueryForm.propTypes = {
	newQueryName: PropTypes.string.isRequired,
	setNewQueryName: PropTypes.func.isRequired,
	errors: PropTypes.object.isRequired,
	setErrors: PropTypes.func.isRequired,
	isProcessing: PropTypes.bool.isRequired,
	isFetchingDetails: PropTypes.bool.isRequired,
	onCreateNew: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default function AddToLeadsModal({ isOpen, onClose, selectedPlaces }) {
	const { data: prospectQueriesResponse, isLoading: loading } = useB2bHeroLeadSearchQueries();

	const addCompanies = useAddCompaniesToProspectQuery();
	const createQuery = useCreateProspectQuery();

	const [selectedQuery, setSelectedQuery] = useState(null);
	const [search, setSearch] = useState('');
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [newQueryName, setNewQueryName] = useState('');
	const [errors, setErrors] = useState({});
	const [showSuccess, setShowSuccess] = useState(false);
	const [isFetchingDetails, setIsFetchingDetails] = useState(false);

	const inputRef = useRef(null);

	// Focus search input when modal opens
	useEffect(() => {
		if (isOpen && inputRef.current && !showCreateForm) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [isOpen, showCreateForm]);

	// Reset form when modal opens/closes
	useEffect(() => {
		if (isOpen) {
			setSelectedQuery(null);
			setSearch('');
			setShowCreateForm(false);
			setNewQueryName('');
			setErrors({});
			setShowSuccess(false);
		}
	}, [isOpen]);

	const filtered = useMemo(() => {
		const queries = prospectQueriesResponse?.data || [];
		return queries.filter((query) => query.queryName.toLowerCase().includes(search.toLowerCase()));
	}, [prospectQueriesResponse, search]);

	const isProcessing = addCompanies.isPending || createQuery.isPending || isFetchingDetails;

	const handleClose = () => {
		if (!isProcessing) onClose();
	};

	const fetchAndTransformPlaces = async () => {
		setIsFetchingDetails(true);
		try {
			const detailedPlaces = await fetchPlacesWithDetails(selectedPlaces);
			return transformPlacesToCompanies(detailedPlaces);
		} finally {
			setIsFetchingDetails(false);
		}
	};

	const handleConfirmExisting = async () => {
		if (!selectedQuery) return;

		try {
			const companies = await fetchAndTransformPlaces();
			await addCompanies.mutateAsync({ prospectQueryId: selectedQuery.id, companies });
			setShowSuccess(true);
			setTimeout(() => handleClose(), 1500);
		} catch {
			setErrors({ general: 'Fehler beim Hinzufügen der Unternehmen' });
		}
	};

	const handleCreateNew = async () => {
		const name = newQueryName.trim();
		const nameError = !name
			? 'Titel ist erforderlich'
			: name.length < 2
				? 'Titel muss mindestens 2 Zeichen haben'
				: null;
		if (nameError) {
			setErrors({ newQueryName: nameError });
			return;
		}

		try {
			const companies = await fetchAndTransformPlaces();

			const result = await createQuery.mutateAsync({
				queryName: name,
				queryTypeCode: 'b2bheroLeadSearch',
				input: { source: 'google_maps_manual', companyCount: companies.length },
			});

			const prospectQuery = result.data;
			if (!prospectQuery?.id) {
				throw new Error('Failed to create prospect query - no ID returned');
			}

			await addCompanies.mutateAsync({ prospectQueryId: prospectQuery.id, companies });
			setSelectedQuery(prospectQuery);
			setShowSuccess(true);
			setTimeout(() => handleClose(), 1500);
		} catch {
			setErrors({ general: 'Fehler beim Erstellen der Suche' });
		}
	};

	const handleCancelCreate = () => {
		setShowCreateForm(false);
		setNewQueryName('');
		setErrors({});
	};

	const successMessage = selectedPlaces.length === 1 ? 'Unternehmen wurde' : 'Unternehmen wurden';
	const emptyListMessage = search ? 'Keine Suchen gefunden' : 'Noch keine Suchen vorhanden';
	const confirmButtonLabel = isProcessing
		? isFetchingDetails
			? 'Lade Details...'
			: 'Wird hinzugefügt...'
		: `${selectedPlaces.length} Unternehmen hinzufügen`;

	if (showSuccess) {
		return (
			<Modal isOpen={isOpen} onClose={handleClose} size="s">
				<Panel.Body>
					<div className="flex flex-col items-center justify-center text-center py-4">
						<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
							<HiCheckCircle className="w-8 h-8 text-success-700" />
						</div>
						<h3 className="text-lg font-semibold mb-2 text-success-700">Erfolgreich hinzugefügt!</h3>
						<p className="text-sm text-text">
							{selectedPlaces.length} {successMessage} zur Suche &quot;
							{selectedQuery?.queryName}&quot; hinzugefügt.
						</p>
					</div>
				</Panel.Body>
			</Modal>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header
				icon={HiOutlineOfficeBuilding}
				onClose={handleClose}
				subtitle={`${selectedPlaces.length} Unternehmen ausgewählt`}
			>
				Zu Leads hinzufügen
			</Panel.Header>
			<Panel.Body>
				{errors.general && (
					<div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
						<p className="text-sm text-warning-700">{errors.general}</p>
					</div>
				)}

				{loading ? (
					<div className="flex justify-center items-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
					</div>
				) : (
					<>
						{!showCreateForm && (
							<div className="mb-4">
								<div className="relative">
									<HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bgBase-800 w-5 h-5" />
									<TextInput
										ref={inputRef}
										className="pl-10"
										placeholder="Suche suchen..."
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
								</div>
							</div>
						)}

						{showCreateForm ? (
							<CreateQueryForm
								newQueryName={newQueryName}
								setNewQueryName={setNewQueryName}
								errors={errors}
								setErrors={setErrors}
								isProcessing={isProcessing}
								isFetchingDetails={isFetchingDetails}
								onCreateNew={handleCreateNew}
								onCancel={handleCancelCreate}
							/>
						) : (
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowCreateForm(true)}
								className="mb-4 w-full"
							>
								<HiPlus className="mr-2" />
								Neue Suche erstellen
							</Button>
						)}

						{!showCreateForm && (
							<div className="border border-bgBase-400 rounded-lg overflow-hidden">
								<div className="max-h-80 overflow-y-auto">
									{filtered.length === 0 ? (
										<div className="p-8 text-center text-bgBase-800">
											<HiOutlineOfficeBuilding className="w-12 h-12 mx-auto mb-2 text-bgBase-800" />
											<p className="text-sm">{emptyListMessage}</p>
										</div>
									) : (
										filtered.map((query) => (
											<QueryListItem
												key={query.id}
												query={query}
												isSelected={selectedQuery?.id === query.id}
												onSelect={setSelectedQuery}
											/>
										))
									)}
								</div>
							</div>
						)}
					</>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={handleClose} disabled={isProcessing}>
					Abbrechen
				</Button>
				<Button variant="solid" disabled={!selectedQuery || isProcessing} onClick={handleConfirmExisting}>
					{isProcessing ? (
						<>
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
							{confirmButtonLabel}
						</>
					) : (
						<>
							<HiOutlineOfficeBuilding className="mr-2" />
							{confirmButtonLabel}
						</>
					)}
				</Button>
			</Panel.Footer>
		</Modal>
	);
}

AddToLeadsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	selectedPlaces: PropTypes.array.isRequired,
};
